import { useQuery } from '@apollo/client/react'
import {
  Box,
  Button,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuArrowLeft, LuSearch } from 'react-icons/lu'
import { useDebounce } from 'react-use'

import { CommunityCard } from '@/components/molecules/Cards/CommunityCard/CommunityCard'
import { RequestToJoinButton } from '@/components/molecules/Circles/RequestToJoinButton'
import { useRequestToJoinCommunities } from '@/components/molecules/Circles/useRequestToJoinCommunities'
import {
  catalogItemFromTemplateLike,
  MaterialCatalogSplitPicker,
} from '@/components/molecules/MaterialCatalogSplitPicker'
import { useCreateMaterialsFromTemplates } from '@/components/views/pooling/my/material/addFromTemplates/useCreateMaterialsFromTemplates'
import {
  DiscoverCommunitiesDocument,
  GetMaterialTemplatesDocument,
} from '@/graphql/api/generated'
import { useAddFromTemplatesStore } from '@/stores/addFromTemplatesStore'
import { useImagePlaceholder } from '@/utils/image'
import { sortCatalogMaterialTemplates } from '@/utils/sortCatalogMaterialTemplates'

interface MemberSubflowProps {
  /** Called when the member finishes (or skips) the whole sub-flow. */
  onComplete: () => void
}

type MemberStep = 'materials' | 'join'

/** Step 1: add first materials from the template catalogue (optional). */
const MaterialsStep = ({
  onNext,
  onSkip,
}: {
  onNext: () => void
  onSkip: () => void
}) => {
  const { t } = useTranslation()
  const noImage = useImagePlaceholder('square')
  const { createMaterials, isSubmitting } = useCreateMaterialsFromTemplates()

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  useDebounce(() => setDebouncedSearchTerm(searchTerm), 500, [searchTerm])

  const {
    availableTemplates,
    selectedTemplates,
    setAllTemplates,
    addTemplate,
    removeTemplate,
    clearSelection,
  } = useAddFromTemplatesStore()

  const { data, loading } = useQuery(GetMaterialTemplatesDocument, {
    variables: { searchTerms: debouncedSearchTerm || '' },
    notifyOnNetworkStatusChange: false,
  })

  const allTemplates = useMemo(() => {
    const nodes =
      data?.templatesMaterials?.edges
        ?.map((edge) => edge?.node)
        .filter(
          (n): n is NonNullable<typeof n> & { name: string } =>
            Boolean(n) && typeof n?.name === 'string'
        ) ?? []
    return sortCatalogMaterialTemplates(nodes)
  }, [data])

  useEffect(() => {
    if (allTemplates.length === 0) {
      return
    }
    const newIds = allTemplates
      .map((tmpl: { id: string }) => tmpl.id)
      .sort()
      .join(',')
    const prevIds = useAddFromTemplatesStore
      .getState()
      .allTemplates.map((tmpl: { id: string }) => tmpl.id)
      .sort()
      .join(',')
    if (newIds !== prevIds) {
      setAllTemplates(allTemplates)
    }
  }, [allTemplates, setAllTemplates])

  const isInitialLoading = loading && availableTemplates.length === 0

  const catalogItems = useMemo(
    () =>
      availableTemplates.map((node) =>
        catalogItemFromTemplateLike(node, noImage)
      ),
    [availableTemplates, noImage]
  )
  const selectedCatalogItems = useMemo(
    () =>
      selectedTemplates.map((node) =>
        catalogItemFromTemplateLike(node, noImage)
      ),
    [selectedTemplates, noImage]
  )

  const handleNext = async () => {
    const ids = selectedTemplates.map((tmpl) => tmpl.id)
    if (ids.length > 0) {
      const ok = await createMaterials(ids)
      if (!ok) {
        return
      }
      clearSelection()
    }
    onNext()
  }

  return (
    <Stack gap="6" w="full" maxW="6xl" mx="auto">
      <Stack gap="1">
        <Heading size={{ base: 'xl', md: '2xl' }}>
          {t('register.wizard.profiles.member.materials.title')}
        </Heading>
        <Text color="fg.muted">
          {t('register.wizard.profiles.member.materials.description')}
        </Text>
      </Stack>

      <MaterialCatalogSplitPicker
        search={{
          label: t('pooling.addFromTemplates.search.label'),
          helperText: t('pooling.addFromTemplates.search.helper'),
          placeholder: t('pooling.addFromTemplates.search.placeholder'),
          value: searchTerm,
          onChange: setSearchTerm,
        }}
        catalogItems={catalogItems}
        loading={loading}
        isInitialLoading={isInitialLoading}
        debouncedSearchTerm={debouncedSearchTerm}
        selectionMode="multiple"
        selectedItems={selectedCatalogItems}
        onCatalogItemClick={(item) => addTemplate(item.id)}
        onRemoveSelected={(item) => removeTemplate(item.id)}
        onClearSelection={clearSelection}
        strings={{
          availableCount: (count: number) =>
            t('pooling.addFromTemplates.availableCount', { count }),
          selectedTitle: t('pooling.addFromTemplates.selected.title'),
          selectedEmpty: t('pooling.addFromTemplates.selected.empty'),
          emptySearchTitle: t('pooling.addFromTemplates.empty.title'),
          emptySearchDescription: t(
            'pooling.addFromTemplates.empty.description'
          ),
          allSelectedTitle: t('pooling.addFromTemplates.allSelected.title'),
          allSelectedDescription: t(
            'pooling.addFromTemplates.allSelected.description'
          ),
          clearSelection: t('pooling.addFromTemplates.clearSelection'),
        }}
        primaryAction={{
          label: t('global:next'),
          onClick: () => void handleNext(),
          loading: isSubmitting,
        }}
        secondaryAction={{
          label: t('register.wizard.profiles.skip'),
          onClick: onSkip,
        }}
      />
    </Stack>
  )
}

/** Step 2: free search of communities to request to join (optional). */
const JoinStep = ({
  onBack,
  onFinish,
}: {
  onBack: () => void
  onFinish: () => void
}) => {
  const { t } = useTranslation()

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  useDebounce(() => setDebouncedSearchTerm(searchTerm), 400, [searchTerm])

  const { isPending, isRequesting, requestToJoin, hasRequested } =
    useRequestToJoinCommunities()

  const { data, loading } = useQuery(DiscoverCommunitiesDocument, {
    variables: { first: 12, searchTerms: debouncedSearchTerm || '' },
  })

  const communities = useMemo(
    () =>
      data?.circles?.edges
        ?.map((edge) => edge.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node)) ??
      [],
    [data]
  )

  return (
    <Stack gap="6" w="full" maxW="6xl" mx="auto">
      <Stack gap="1">
        <Heading size={{ base: 'xl', md: '2xl' }}>
          {t('register.wizard.profiles.member.join.title')}
        </Heading>
        <Text color="fg.muted">
          {t('register.wizard.profiles.member.join.description')}
        </Text>
      </Stack>

      <Box position="relative">
        <Input
          size="lg"
          aria-label={t(
            'register.wizard.profiles.member.join.searchPlaceholder'
          )}
          placeholder={t(
            'register.wizard.profiles.member.join.searchPlaceholder'
          )}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        <Box
          position="absolute"
          right="3"
          top="50%"
          transform="translateY(-50%)"
        >
          <LuSearch />
        </Box>
      </Box>

      {loading ? (
        <HStack justify="center" py="8">
          <Spinner />
        </HStack>
      ) : communities.length === 0 ? (
        <Text color="fg.muted" textAlign="center" py="8">
          {t('register.wizard.profiles.member.join.empty')}
        </Text>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap="6">
          {communities.map((circle) => (
            <CommunityCard
              key={circle.id}
              circle={circle}
              isFullHeight
              // Onboarding runs in a full-screen dialog: a card link would swap the route
              // underneath it and strand the user on a screen that never changes.
              disableNavigation
              footer={
                <RequestToJoinButton
                  circleId={circle.id}
                  circleName={circle.name}
                  isPending={isPending(circle.id)}
                  isRequesting={isRequesting(circle.id)}
                  onRequest={requestToJoin}
                />
              }
            />
          ))}
        </SimpleGrid>
      )}

      <HStack justify="space-between" pt="2">
        <Button variant="ghost" onClick={onBack}>
          <LuArrowLeft />
          {t('register.wizard.profiles.back')}
        </Button>
        <Button
          size="lg"
          rounded="full"
          colorPalette="brand"
          onClick={onFinish}
        >
          {hasRequested
            ? t('register.wizard.profiles.member.join.finish')
            : t('register.wizard.profiles.skip')}
        </Button>
      </HStack>
    </Stack>
  )
}

/**
 * "Je participe" sub-flow: add first materials, then optionally request to join
 * communities. Both steps are skippable (no dead-end for an isolated new user).
 */
export const MemberSubflow = ({ onComplete }: MemberSubflowProps) => {
  const [step, setStep] = useState<MemberStep>('materials')

  // Start from a clean slate: the template store is a session singleton shared
  // with the standalone add-from-templates page, so a prior selection could
  // otherwise leak into onboarding. Runs once (internal step switches keep it).
  useEffect(() => {
    useAddFromTemplatesStore.getState().clearSelection()
  }, [])

  if (step === 'materials') {
    return (
      <MaterialsStep
        onNext={() => setStep('join')}
        onSkip={() => setStep('join')}
      />
    )
  }

  return <JoinStep onBack={() => setStep('materials')} onFinish={onComplete} />
}
